import React, { Component } from 'react';
import { PropTypes as PT } from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import _compact from 'lodash/compact';
import SVG from 'react-inlinesvg';
import moreIcon from '@lifesize/ux-assets/vectors/svgIcons/more.svg';
import personIcon from '@lifesize/ux-assets/vectors/svgIcons/person.svg';
import recordIcon from '@lifesize/ux-assets/vectors/svgIcons/record.svg';
import recordStopIcon from '@lifesize/ux-assets/vectors/svgIcons/record-stop.svg';
import videoIcon from '@lifesize/ux-assets/vectors/svgIcons/video.svg';
import videoOffIcon from '@lifesize/ux-assets/vectors/svgIcons/video-off.svg';
import Tooltip from 'components/Tooltip/Tooltip';
import DialPadComponent from 'components/DialPad/DialPad';
import RaiseHandButton from 'components/InCall/InCallFooterButtons/RaiseHandButton';
import { MUTE_VIDEO, STOP_RECORDING, START_RECORDING } from 'constants/lsqaConstants';
import PresentationButton from 'containers/InCall/PresentationButton/PresentationButtonContainer';
import OtherOptions from 'containers/InCall/OtherOptions/OtherOptionsContainer';
import FarEndCameraControls from 'containers/FarEndCameraControls/FarEndCameraControlsContainer';
import AudioButton from 'containers/AudioButton/AudioButtonContainer';
import EndCallButton from 'containers/InCall/EndCallButtonContainer';
import CallDuration from './CallDuration';
import classes from './CallFooter.module.scss';

export class CallFooter extends Component {
  renderVideoMuteButton() {
    let button = null;
    if (this.props.isConnected) {
      if (this.props.activeCall.isVideoMuted) {
        button = (
          <button
            className={classes.inCallButtonActive}
            onClick={() => this.props.onVideoMuteClick()}
            {...MUTE_VIDEO}
            data-testid="unmute-video-btn"
          >
            <SVG src={videoOffIcon} data-testid="videoOffIcon" />
            <Tooltip
              location="top"
              style={{ bottom: '100%', marginBottom: 15 }} label={(
                <FormattedMessage
                  id="components.incall.controls.videoMute.tooltip.on"
                  defaultMessage="Turn camera on"
                />
              )}
            />
          </button>
        );
      } else {
        const activeVideoClasses = [
          classes.inCallButton,
          classes.activeVideoButton
        ].join(' ');

        button = (
          <button
            className={activeVideoClasses}
            onClick={() => this.props.onVideoMuteClick()}
            {...MUTE_VIDEO}
            data-testid="mute-video-btn"
          >
            <SVG src={videoIcon} data-testid="videoIcon" />
            <Tooltip
              location="top"
              style={{ bottom: '100%', marginBottom: 15 }} label={(
                <FormattedMessage
                  id="components.incall.controls.videoMute.tooltip.off"
                  defaultMessage="Turn camera off"
                />
              )}
            />
          </button>
        );
      }
    }
    return button;
  }

  renderRecordingButton() {
    let button = null;
    if (this.props.isConnected && !this.props.isGuest && this.props.activeCall.recordingEnabled) {
      if (this.props.isRecording) {
        let label;

        if (this.props.canStopRecording) {
          label = (<FormattedMessage
            id="components.incall.controls.record.tooltip.off"
            defaultMessage="Stop recording"
          />);
        } else if (this.props.recorder) {
          label = (<FormattedMessage
            id="components.incall.footer.recording.cantStop"
            defaultMessage="Recording started by {name}"
            values={{
              name: this.props.recorder.name
            }}
          />);
        } else {
          label = (<FormattedMessage
            id="components.incall.footer.recording.cantStopUnknown"
            defaultMessage="Recording started"
          />);
        }

        button = (
          <button
            className={classes.inCallButtonActive}
            onClick={() => this.props.onStopRecording()}
            disabled={!this.props.canStopRecording}
            {...STOP_RECORDING}
            data-testid="stop-recording-btn"
          >
            <SVG src={recordStopIcon} className={classes.recordStop} data-testid="recordStopIcon" />
            <Tooltip
              location="top"
              style={{ bottom: '100%', marginBottom: 15 }} label={label}
            />
          </button>
        );
      } else {
        button = (
          <button
            className={classes.inCallButton}
            onClick={() => this.props.onStartRecording()}
            {...START_RECORDING}
            data-testid="start-recording-btn"
          >
            <SVG src={recordIcon} data-testid="recordIcon" />
            <Tooltip
              location="top"
              style={{ bottom: '100%', marginBottom: 15 }} label={(
                <FormattedMessage
                  id="components.incall.controls.record.tooltip.on"
                  defaultMessage="Start recording"
                />
              )}
            />
          </button>
        );
      }
    }
    return button;
  }

  renderKebabButton() {
    let button = null;
    if (this.props.isConnected) {
      button = (
        <div className={classes.kebabContainer}>
          <button
            className={classes.kebabButton}
            data-testid="kebab-btn"
            id="inCallKebabBtn"
            onClick={() => this.props.onKebabClicked()}
          >
            <SVG src={moreIcon} data-testid="moreIcon" />
          </button>
          <OtherOptions />
        </div>
      );
    }
    return button;
  }

  renderPip() {
    const containerClass = _compact([
      classes.pipContainer,
      this.props.pipExpanded ? classes.expanded : '',
      this.props.activeCall.isVideoMuted && !this.props.selfPresenting ? classes.videoMuted : ''
    ]).join(' ');

    const pipClass = _compact([
      classes.pip,
      this.props.selfPresenting ? classes.hiddenPrimaryVideo : ''
    ]).join(' ');

    return (
      <div className={containerClass} style={this.props.isMinimized ? { display: 'none' } : null}>
        <button className={classes.pipDrawer} onClick={() => { this.props.togglePip(); }}>
          <div className={classes.pipIcon} />
          <div className={classes.arrow} />
          <div className={pipClass}>
            <SVG src={personIcon} data-testid="personIcon" />
            <video
              className={classes.invertVideo}
              id={this.props.videoElementId}
            />
            <video
              id={this.props.videoPresentationId}
            />
          </div>
          <Tooltip
            label={this.props.pipExpanded
              ? <FormattedMessage id="contacts.tooltip.pip.hide" defaultMessage="Hide" />
              : <FormattedMessage id="contacts.tooltip.pip.show" defaultMessage="Show" />
            }
            location="right"
            overlapCarat={false}
            style={{ marginLeft: 3 }}
          />
        </button>
      </div>
    );
  }

  renderCenterContainer() {
    const centerContainerClasses = _compact([
      classes.centerContainer,
      !this.props.showRaiseHandsButton && classes.compact
    ]).join(' ');
    return this.props.isMinimized
      ? (
        <div className={classes.minimizedCenterContainer}>
          <AudioButton iconClass={classes.icon} />
          {this.props.isConnected &&
            <EndCallButton />
          }
        </div>
      )
      : (
        <div className={centerContainerClasses}>
          <AudioButton iconClass={classes.icon} />
          {this.renderVideoMuteButton()}
          <div className={classes.verticalDivide} />
          <PresentationButton />
          {this.renderRecordingButton()}
          {this.props.showRaiseHandsButton &&
            <RaiseHandButton
              disabled={!this.props.canRaiseHand}
              handIsProcessing={this.props.handIsProcessing}
              handIsRaised={this.props.handIsRaised}
              isConnected={this.props.isConnected}
              notification={this.props.btnNotificationRaisedHand}
              onRaiseHand={this.props.onRaiseHand}
            />
          }
        </div>
      );
  }

  renderRightContainer() {
    return this.props.isMinimized ? (
      <div className={classes.minimizedRightContainer}>
        <CallDuration startTime={this.props.activeCall.startTime} />
      </div>
    ) :
      (
        <div className={classes.rightContainer}>
          <CallDuration startTime={this.props.activeCall.startTime} />
          {this.props.isConnected &&
            <EndCallButton />
          }
          {this.renderKebabButton()}
          {this.props.showCameraControls &&
            <FarEndCameraControls />
          }
          <DialPadComponent
            isVisible={this.props.showDialPad}
            handleClose={this.props.toggleDialPad}
            addNumber={this.props.onDTMFClicked}
          />
        </div>
      );
  }

  render() {
    const footerStyle = !this.props.videoLayout.isDualStreaming && !this.props.isMinimized
      ? { width: this.props.videoWidth || '100%' }
      : {};

    return (
      <div
        className={this.props.isMinimized ?
          classes.minimizedFooterContainer : classes.footerContainer}
        style={footerStyle}
      >
        {this.renderPip()}
        {this.renderCenterContainer()}
        {this.renderRightContainer()}
      </div>
    );
  }
}

CallFooter.propTypes = {
  activeCall: PT.object,
  btnNotificationRaisedHand: PT.object,
  canRaiseHand: PT.bool,
  canStopRecording: PT.bool,
  handIsProcessing: PT.bool,
  handIsRaised: PT.bool,
  intl: PT.object,
  isConnected: PT.bool.isRequired,
  isGuest: PT.bool,
  isMinimized: PT.bool,
  isRecording: PT.bool,
  onDTMFClicked: PT.func.isRequired,
  onKebabClicked: PT.func.isRequired,
  onRaiseHand: PT.func.isRequired,
  onStartRecording: PT.func.isRequired,
  onStopRecording: PT.func.isRequired,
  onVideoMuteClick: PT.func.isRequired,
  pipExpanded: PT.bool.isRequired,
  recorder: PT.object,
  selfPresenting: PT.bool.isRequired,
  showCameraControls: PT.bool,
  showDialPad: PT.bool.isRequired,
  toggleDialPad: PT.func.isRequired,
  togglePip: PT.func.isRequired,
  videoElementId: PT.string.isRequired,
  videoLayout: PT.object,
  videoPresentationId: PT.string.isRequired,
  videoWidth: PT.number,
  showRaiseHandsButton: PT.bool
};

export default injectIntl(CallFooter);
